import { Box, Checkbox, FormControlLabel, Grid, Typography } from '@mui/material';
import dayjs from 'dayjs';
import React from 'react';
import { createDesignationsLookupHeaders } from '../../../../constants/apiHeaders';
import { PAGE_IDS } from '../../../../constants/permissions';
import api from '../../../../services/httpClient';
import { SYSTEM_ENDPOINTS } from '../../../../services/endpoints/system.endpoints';
import { AsyncAutocomplete } from '../../../common/AsyncAutocomplete';
import DatePickerField from '../../../common/DatePickerField';
import SelectField from '../../../common/SelectField';
import TextField from '../../../common/TextField';
import type { BasicEntry } from '../../../../services/types/vacancy.types';

export type { BasicEntry };

interface VacancyBasicCardProps {
  basicData: BasicEntry;
  type: 'add' | 'edit' | 'view';
  errors: Record<string, string>;
  setBasicData: (data: BasicEntry) => void;
  setErrors: (errors: Record<string, string>) => void;
}

const VacancyBasicCard: React.FC<VacancyBasicCardProps> = ({ type, errors, setErrors, basicData, setBasicData }) => {
  const isVacancyCodeLocked = type === 'edit' && basicData.jobPostPublishedStatus;

  const vacancyTypeOptions = [
    { value: 'TAAS', label: 'TAAS' },
    { value: 'EOR', label: 'EOR' },
  ];

  const vacancyReasonOptions = [
    { value: 'Replacement', label: 'Replacement' },
    {
      value: 'Confirmed Billable Requirement',
      label: 'Confirmed Billable Requirement',
    },
    { value: 'Market Research', label: 'Market Research' },
  ];

  const handleLoadDesignations = async (query: string) => {
    const response = await api.get<{
      data: { id: number; designation: string }[];
    }>(SYSTEM_ENDPOINTS.designations, {
      headers: { ...createDesignationsLookupHeaders(PAGE_IDS.VACANCY_ADD) },
    });

    return response.data.data.map((item: any) => ({
      value: item.id.toString(),
      label: item.designation,
    }));
  };

  const handleInputChange = (field: keyof BasicEntry, value: any) => {
    setBasicData({
      ...basicData,
      [field]: value,
    });
    setErrors({
      ...errors,
      [field]: '',
    });
  };

  return (
    <Box className="edit-section">
      <Grid container spacing={2}>
        {/* First Row */}
        <Grid container spacing={8}>
          <Grid>
            <SelectField
              label="Vacancy Type"
              options={vacancyTypeOptions}
              width="360px"
              name="vacancyType"
              value={basicData.vacancyType}
              onChange={(e) => handleInputChange('vacancyType', e.target.value)}
              error={!!errors.vacancyType}
              required={true}
              helperText={errors.vacancyType}
            />
          </Grid>
          <Grid>
            <TextField
              label="Vacancy Title"
              width="360px"
              name="vacancyTitle"
              value={basicData.vacancyTitle}
              onChange={(e) =>
                handleInputChange('vacancyTitle', e.target.value)
              }
              error={!!errors.vacancyTitle}
              required={true}
              helperText={errors.vacancyTitle}
            />
          </Grid>
          <Grid>
            <TextField
              label="Vacancy Code"
              width="360px"
              name="vacancyCode"
              value={basicData.vacancyCode}
              onChange={(e) => handleInputChange('vacancyCode', e.target.value)}
              disabled={isVacancyCodeLocked}
              error={!!errors.vacancyCode}
              required={true}
              helperText={errors.vacancyCode || (isVacancyCodeLocked ? "Vacancy code cannot be changed after publishing" : "")}
            />
          </Grid>
          <Grid />
        </Grid>

        {/* Second Row */}
        <Grid container spacing={8}>
          <Grid>
            <TextField
              label="Minimum Industry Experience"
              width="360px"
              type="number"
              name="minIndustryExperience"
              value={basicData.minIndustryExperience}
              onChange={(e) =>
                handleInputChange('minIndustryExperience', e.target.value)
              }
              error={!!errors.minIndustryExperience}
              required={true}
              helperText={errors.minIndustryExperience}
            />
          </Grid>
          <Grid>
            <DatePickerField
              label="Start Date"
              value={basicData.startDate ? dayjs(basicData.startDate) : null}
              onChange={(date) => handleInputChange('startDate', date)}
              width="360px"
              required={true}
              helperText={errors.startDate}
            />
          </Grid>
          <Grid>
            <DatePickerField
              label="End Date"
              value={basicData.endDate ? dayjs(basicData.endDate) : null}
              onChange={(date) => handleInputChange('endDate', date)}
              width="360px"
              required={true}
              helperText={errors.endDate}
            />
          </Grid>
          <Grid />
        </Grid>

        {/* Third Row */}
        <Grid container spacing={8}>
          <Grid>
            <SelectField
              label="Vacancy Reason"
              options={vacancyReasonOptions}
              width="360px"
              name="vacancyReason"
              value={basicData.vacancyReason}
              onChange={(e) =>
                handleInputChange('vacancyReason', e.target.value)
              }
              error={!!errors.vacancyReason}
              required={true}
              helperText={errors.vacancyReason}
            />
          </Grid>
          <Grid>
            <AsyncAutocomplete
              label="Designation"
              width="360px"
              loadOptions={handleLoadDesignations}
              value={basicData.designation}
              onChange={(value) => handleInputChange('designation', value)}
              error={!!errors.designation}
              helperText={errors.designation}
              required={false}
            />
          </Grid>
          <Grid>
            <DatePickerField
              label="Deadline"
              value={basicData.dueDate ? dayjs(basicData.dueDate) : null}
              onChange={(date) => handleInputChange('dueDate', date)}
              width="360px"
              required={true}
              helperText={errors.dueDate}
            />
          </Grid>
          <Grid />
        </Grid>

        {/* Fourth Row */}
        <Grid container spacing={8}>
          <Grid>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography component="label" className="input-field-label">
                Status
              </Typography>
            </Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={basicData.isActive}
                  onChange={(e) =>
                    handleInputChange('isActive', e.target.checked)
                  }
                  name="isActive"
                  sx={{
                    color: '#0D9C33',
                    '&.Mui-checked': {
                      color: '#0D9C33',
                    },
                  }}
                />
              }
              label="Active"
              sx={{
                color: '#00000066',
                fontSize: '12px',
                fontWeight: 500,
                marginTop: '-7px',
              }}
            />
          </Grid>
          <Grid>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography component="label" className="input-field-label">
                Visibility
              </Typography>
            </Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={basicData.isVisible}
                  onChange={(e) => handleInputChange('isVisible', e.target.checked)}
                  name="isVisible"
                  sx={{
                    color: '#0D9C33',
                    '&.Mui-checked': {
                      color: '#0D9C33',
                    },
                  }}
                />
              }
              label="Visible"
              sx={{
                color: '#00000066',
                fontSize: '12px',
                fontWeight: 500,
                marginTop: '-7px',
              }}
            />
          </Grid>
          <Grid />
          <Grid />
          <Grid />
        </Grid>
      </Grid>
    </Box>
  );
};

export default VacancyBasicCard;
